import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { McpWidget } from "../../../sandbox/components";
import { GET_MCP_CONNECTION } from "../../../api/graphql/mcp";
import { useAccountAccess } from "../../../hooks/useAccountAccess";
import { triggerGlobalNotifications } from "src/context/ErrorBridge";

type McpToken = {
  client: string | null;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
};

// An OAuth connector trades its hour-long access token for a new one for
// thirty days from the last exchange (REFRESH_TOKEN_TTL_MS on the backend),
// so its row counts while that window is open, not just for the hour.
const OAUTH_CONNECTION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Connected: a client actually talks to the account — an OAuth connector whose
 * sign-in is still live, or a key from Settings that has made a call and has
 * not expired. A key issued and never used connects nothing yet.
 */
export const isMcpConnected = (tokens: McpToken[], now = Date.now()) =>
  tokens.some((token) => {
    if (token.client) {
      return Date.parse(token.createdAt) + OAUTH_CONNECTION_MS > now;
    }
    if (!token.lastUsedAt) return false;
    return !token.expiresAt || Date.parse(token.expiresAt) > now;
  });

/** Header container for the MCP widget: status, endpoint, copy. */
export const McpStatus: React.FC = () => {
  // The keys are the owner's (OwnerOnlyGuard): a teammate's query would only fail.
  const { isOwnAccount } = useAccountAccess();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const { data, refetch } = useQuery(GET_MCP_CONNECTION, {
    skip: !isOwnAccount,
  });

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  // No answer yet, or a backend without the MCP endpoints: an empty spot
  // beats a pill that says "Not connected" and then changes its mind.
  const connection = data?.mcpConnection;
  if (!isOwnAccount || !connection?.endpoint) return null;

  const onToggle = () => {
    // A key issued in Settings since the header loaded shows up on opening.
    if (!open) void refetch();
    setOpen(!open);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(connection.endpoint);
      setCopied(true);
    } catch {
      triggerGlobalNotifications({
        type: "error",
        data: { message: "Could not copy the endpoint", placement: "top" },
      });
    }
  };

  return (
    <div ref={rootRef}>
      <McpWidget
        connected={isMcpConnected(connection.tokens ?? [])}
        endpoint={connection.endpoint}
        open={open}
        onToggle={onToggle}
        onCopy={onCopy}
        copied={copied}
      />
    </div>
  );
};

export default McpStatus;
